// Person registry detail page. Renders the shared read-only PersonDetailView
// (which itself gates physical/PII/government-ID/history sections on the
// viewer's permissions), wired with:
//   - history     — fetched via usePersonHistory only when can('person.read_history')
//   - reveal      — a closure over useRevealPersonGovernmentId, supplied only
//                   when can('person.read_government_ids')
//   - Edit        — gated can('person.write'); opens the shared PersonForm,
//                   prefilled from the detail. See the EDIT SCOPE note below.
//   - Delete      — gated can('person.delete'); soft-delete behind a confirm.
//
// EDIT SCOPE: the server has NO bulk "replace-all satellites" endpoint, and the
// PersonForm always emits full satellite arrays (it cannot express per-row
// create/update/delete diffs against the existing rows). Computing a safe diff
// from the form payload back to server rows is non-trivial. So this page takes
// the simpler, documented approach: the Edit modal updates ONLY the person
// scalar fields (via useUpdatePerson). Satellite arrays returned by the form on
// edit are intentionally ignored here; address/phone/email/emergency-contact
// editing is expected to happen through dedicated per-row actions (the
// satellite CRUD hooks already exist), not this form. On create (PersonsPage)
// the satellites ARE persisted, because there are no pre-existing rows to diff.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { UpdatePersonInput } from '@dsr/shared-types';
import {
  useDeletePerson,
  usePerson,
  usePersonHistory,
  useRevealPersonGovernmentId,
  useUpdatePerson,
} from '../api/queries/persons';
import { PersonDetailView } from '../components/PersonDetailView';
import { PersonEmailsManager } from '../components/PersonEmailsManager';
import { PersonPhoto } from '../components/PersonPhoto';
import { PersonForm, type PersonFormPayload } from '../components/PersonForm';
import { Button, ConfirmDialog, Dialog } from '../components/ui';
import { MailboxCard } from '../components/MailboxCard';
import { toast } from '../components/Toasts';
import { can, geoapifyKey } from '../auth/store';
import { ApiError } from '../api/client';

export function PersonDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const personId = id ? Number(id) : null;

  const { data, isLoading, isError, error } = usePerson(personId);

  const canHistory = can('person.read_history');
  const canGovIds = can('person.read_government_ids');
  const canWrite = can('person.write');
  const canDelete = can('person.delete');

  const { data: historyData } = usePersonHistory(personId, canHistory);
  const reveal = useRevealPersonGovernmentId();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeletePerson();

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="error-text" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load person.'}
      </p>
    );
  }

  const person = data.person;

  const onRevealGovernmentId =
    canGovIds && personId !== null
      ? async (govId: number): Promise<string> => {
          const res = await reveal.mutateAsync({ personId, govId });
          return res.number;
        }
      : undefined;

  const onDelete = async (): Promise<void> => {
    if (personId === null) return;
    try {
      await del.mutateAsync(personId);
      toast.success('Person deleted.');
      setConfirmDelete(false);
      navigate('/persons');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to delete person.');
    }
  };

  return (
    <div className="page person-detail-page">
      <nav className="breadcrumbs">
        <Link to="/persons">People Directory</Link>
        <span aria-hidden="true"> / </span>
        <span>{person.full_name}</span>
      </nav>

      <header className="page-header">
        <div className="person-detail-heading">
          {personId !== null && (
            <PersonPhoto
              personId={personId}
              photoPath={person.photo_path}
              fullName={person.full_name}
            />
          )}
          <div>
            <h1>{person.full_name}</h1>
            {person.preferred_name && (
              <p className="page-sub">Preferred name: {person.preferred_name}</p>
            )}
          </div>
        </div>
        <div className="header-actions">
          {canWrite && (
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      <PersonDetailView
        person={person}
        history={historyData?.history ?? null}
        onRevealGovernmentId={onRevealGovernmentId}
        hideEmails={canWrite && person.emails !== undefined}
      />

      {canWrite && person.emails !== undefined && personId !== null && (
        <PersonEmailsManager personId={personId} emails={person.emails} />
      )}

      {personId !== null && (
        <MailboxCard
          personId={personId}
          hasPrimaryPersonalEmail={
            person.emails?.some(
              (e) => e.is_primary && !e.is_company_managed && (e.label === 'personal' || e.label === 'other'),
            ) ?? false
          }
        />
      )}

      {editOpen && personId !== null && (
        <EditPersonModal
          open={editOpen}
          personId={personId}
          initial={person}
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete person"
        message="This soft-deletes the person record. Continue?"
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// =============================================================================
// Edit person modal — see the EDIT SCOPE note at the top of the file. Only the
// person scalar fields (payload.person) are persisted here, via useUpdatePerson.
// The satellite arrays the form emits are intentionally not applied on edit.
// =============================================================================

interface EditModalProps {
  open: boolean;
  personId: number;
  initial: import('@dsr/shared-types').PersonDetail;
  onClose: () => void;
}

function EditPersonModal({ open, personId, initial, onClose }: EditModalProps): JSX.Element {
  const update = useUpdatePerson();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (payload: PersonFormPayload): Promise<void> => {
    setFormError(null);
    setSubmitting(true);
    try {
      // Only scalar person fields are applied on edit (satellites are managed
      // via their own per-row actions; the form cannot express a row diff).
      const patch: UpdatePersonInput = { ...payload.person };
      await update.mutateAsync({ id: personId, ...patch });
      toast.success('Person updated.');
      onClose();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to update person.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog title="Edit person" open={open} onClose={onClose} busy={submitting} size="wide">
      {formError && (
        <p className="form-error error-text" role="alert">
          {formError}
        </p>
      )}
      <PersonForm
        initial={initial}
        onSubmit={onSubmit}
        submitting={submitting}
        geoapifyKey={geoapifyKey()}
      />
    </Dialog>
  );
}
